import type { FC } from 'react'
import { useState } from 'react'

import { Checkbox, Card, Popup, Select } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field } from 'formik'
import { uniqBy } from 'lodash'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useIsAdmin } from '@/hooks/access'
import type { VerifyCategoriesByLeasingVendor } from '@/modules/ecom-policy/hooks'
import { leasingResources } from '@/resources/leasing'
import { commonQueries } from '@/resources/queries'

import type { FormFields } from './types'

export const Flags: FC<{
  isActive: boolean
  customerId?: number | null
  hideBuyout?: boolean
  isErrorOnLeasingVendorUpdate?: boolean
  isDisabled?: boolean
  verifyLeasingVendorCategories: (acc: VerifyCategoriesByLeasingVendor) => void
}> = ({
  isActive,
  customerId,
  hideBuyout,
  isErrorOnLeasingVendorUpdate,
  isDisabled = false,
  verifyLeasingVendorCategories,
}) => {
  const translate = useTranslation()
  const isAdmin = useIsAdmin()
  const [isInactivationWarningModalOpen, setIsInactivationWarningModalOpen] = useState(false)
  const { data: customerVendors } = useQuery(
    commonQueries.customerVendorsList(),
    leasingResources.getCustomerVendors
  )

  // Check if the vendor for the given customer has a policy linked to them
  const isLeasingEnabled =
    Array.isArray(customerVendors) &&
    (customerId
      ? customerVendors?.filter(v => v.customerId === customerId).some(v => v.usePolicyLevelLeasing)
      : customerVendors?.some(v => v.usePolicyLevelLeasing))

  const isBuyoutAllowedDisabled = (formValues: FormFields) =>
    isDisabled || !formValues.isAssetReturnRequired || (isAdmin && formValues.isLeasing)

  return (
    <Card className="flags">
      <h3>
        <Translation id="Misc" />
      </h3>

      <Field name="isManagerApprovalRequired">
        {({ field }: FieldProps) => (
          <Checkbox
            id="isManagerApprovalRequired"
            label={<Translation id="Manager approval required" />}
            marginBottom={16}
            isDisabled={isDisabled}
            checked={field.value}
            {...field}
          />
        )}
      </Field>

      <Field name="isCustomAddressActivated">
        {({ field }: FieldProps) => (
          <Checkbox
            id="isCustomAddressActivated"
            label={<Translation id="Custom delivery address" />}
            marginBottom={16}
            isDisabled={isDisabled}
            checked={field.value}
            {...field}
          />
        )}
      </Field>

      <Field name="isAssetReturnRequired">
        {({ field, form }: FieldProps) => (
          <Checkbox
            id="isAssetReturnRequired"
            label={<Translation id="Is asset return required" />}
            marginBottom={16}
            isDisabled={isDisabled}
            checked={field.value}
            onFieldChange={() => {
              if (!form.values.isBuyoutAllowed || !field.value) return
              form.setFieldValue('isBuyoutAllowed', false)
            }}
            {...field}
          />
        )}
      </Field>

      {!hideBuyout && (
        <Field name="isBuyoutAllowed">
          {({ field, form }: FieldProps) => (
            <Checkbox
              id="isBuyoutAllowed"
              label={<Translation id="Is buyout allowed" />}
              marginBottom={16}
              checked={field.value}
              isDisabled={isBuyoutAllowedDisabled(form.values)}
              {...field}
            />
          )}
        </Field>
      )}

      <Field name="isActive">
        {({ field }: FieldProps) => (
          <Checkbox
            id="isActive"
            label={<Translation id="Is active" />}
            marginBottom={16}
            checked={field.value}
            isDisabled={isDisabled}
            {...field}
            onChange={e => {
              if (isActive && field.value) {
                setIsInactivationWarningModalOpen(true)
              }
              field.onChange(e)
            }}
          />
        )}
      </Field>

      {isLeasingEnabled && (
        <LeaseBlock
          isLeasingEnabled={isLeasingEnabled}
          customerId={customerId}
          isErrorOnLeasingVendorUpdate={isErrorOnLeasingVendorUpdate}
          isDisabled={isDisabled}
          verifyLeasingVendorCategories={verifyLeasingVendorCategories}
        />
      )}

      <Popup
        isOpen={isInactivationWarningModalOpen}
        ariaLabel={translate('Inactive policy warning')}
        onClose={() => setIsInactivationWarningModalOpen(false)}
      >
        <>
          <h3>
            <Translation id="Warning" />
          </h3>

          <p>
            <Translation
              id="If you update and save this policy as inactive, all policy subscribers will be unsubscribed
              and unable to order more products from this policy's selection."
            />
          </p>
        </>
      </Popup>
    </Card>
  )
}

const LeaseBlock: FC<{
  isLeasingEnabled: boolean
  customerId?: number | null
  isErrorOnLeasingVendorUpdate?: boolean
  isDisabled?: boolean
  verifyLeasingVendorCategories: (acc: VerifyCategoriesByLeasingVendor) => void
}> = ({
  isLeasingEnabled,
  customerId,
  isErrorOnLeasingVendorUpdate = false,
  isDisabled = false,
  verifyLeasingVendorCategories,
}) => {
  const translate = useTranslation()
  const { data, isLoading } = useQuery(
    commonQueries.customerVendorsList(),
    leasingResources.getCustomerVendors,
    { enabled: isLeasingEnabled }
  )

  const filteredVendors =
    data?.filter(v => {
      if (customerId) {
        return v.customerId === customerId && v.usePolicyLevelLeasing
      }
      return v.usePolicyLevelLeasing
    }) || []
  const uniqueVendorList = uniqBy(filteredVendors, 'vendorId')

  return (
    <>
      <div className="my-4 block h-px w-full bg-gray-500 opacity-20" />
      <Field name="isLeasing">
        {({ field, form }: FieldProps) => (
          <Checkbox
            id="isLeasing"
            label={<Translation id="Lease" />}
            marginBottom={16}
            checked={field.value}
            isDisabled={isDisabled || !isLeasingEnabled || filteredVendors.length === 0}
            {...field}
            onChange={e => {
              form.setFieldValue(field.name, e.target.checked)
              if (e.target.checked) {
                form.setFieldTouched('leasingVendorId')
              }
              if (e.target.checked && form.values.isBuyoutAllowed) {
                form.setFieldValue('isBuyoutAllowed', false)
              }
              verifyLeasingVendorCategories({
                isLeasing: e.target.checked,
                leasingVendorId: form.values.leasingVendorId,
                vendors: uniqueVendorList,
              })
            }}
          />
        )}
      </Field>

      <Field name="leasingVendorId">
        {({ field, form, meta }: FieldProps) =>
          form.values.isLeasing && (
            <Select
              id="leasingVendorId"
              options={uniqueVendorList.map(vendor => ({
                label:
                  vendor.name +
                  (vendor.customerId
                    ? ` (${translate('org. nbr.:')} ${vendor.customerOrgNumber})`
                    : ''),
                value: vendor.vendorId,
              }))}
              label={
                <>
                  <Translation id="Leasing vendor" />
                  {form.values.isLeasing && '*'}
                </>
              }
              placeholder={translate('Please, select a leasing vendor')}
              isLoading={isLoading}
              isDisabled={isDisabled}
              marginBottom={16}
              error={meta.error}
              touched={meta.touched}
              {...field}
              onChange={option => {
                form.setFieldValue(field.name, option?.value)
                if (option) {
                  verifyLeasingVendorCategories({
                    isLeasing: form.values.isLeasing,
                    leasingVendorId: option.value,
                    vendors: uniqueVendorList,
                  })
                }
              }}
            />
          )
        }
      </Field>

      {isErrorOnLeasingVendorUpdate && (
        <div className="mt-4">
          <Translation
            id="We couldn't update the leasing vendor. Please try again later."
            className="text-red-500"
          />
        </div>
      )}
    </>
  )
}
